import type {AvatarFilter} from '@contentlab/types/avatarFilters';

// Base filter definitions - shared across different avatar types
export const AVATAR_FILTERS: Record<
  string,
  Omit<AvatarFilter, 'selectedOptionId'>
> = {
  gender: {
    id: 'gender',
    label: 'Gender',
    options: [
      {id: 'Feminine', label: 'Feminine'},
      {id: 'Masculine', label: 'Masculine'},
      {id: 'Neutral', label: 'Neutral'},
    ],
  },
  age: {
    id: 'age',
    label: 'Age',
    options: [
      {id: 'Young Adults', label: 'Young Adults (18-39)'},
      {id: 'Middle-Aged Adults', label: 'Middle-Aged Adults (40-64)'},
      {id: 'Seniors', label: 'Seniors (65+)'},
    ],
  },
  role: {
    id: 'role',
    label: 'Role',
    options: [
      {id: 'Construction', label: 'Construction'},
      {id: 'Corporate & Office: Casual', label: 'Corporate & Office: Casual'},
      {id: 'Corporate & Office: Formal', label: 'Corporate & Office: Formal'},
      {id: 'Education', label: 'Education'},
      {id: 'General Occupations', label: 'General Occupations'},
      {id: 'Healthcare & Lab', label: 'Healthcare & Lab'},
      {id: 'Hospitality & Food', label: 'Hospitality & Food'},
      {id: 'Public Services', label: 'Public Services'},
      {id: 'Retail', label: 'Retail'},
      {id: 'None', label: 'Other'},
    ],
  },
  presentationStyle: {
    id: 'presentationStyle',
    label: 'Style',
    options: [
      {id: 'Formal', label: 'Formal'},
      {id: 'Natural', label: 'Natural'},
      {id: 'Playful', label: 'Playful'},
    ],
  },
  culturalRepresentation: {
    id: 'culturalRepresentation',
    label: 'Region',
    options: [
      {id: 'Africa', label: 'Africa'},
      {id: 'East Asia', label: 'East Asia'},
      {id: 'Europe', label: 'Europe'},
      {id: 'Latin America', label: 'Latin America'},
      {id: 'Middle East', label: 'Middle East'},
      {id: 'South & Southeast Asia', label: 'South & Southeast Asia'},
    ],
  },
  accent: {
    id: 'accent',
    label: 'Accent',
    options: [
      {id: 'african american', label: 'African American'},
      {id: 'american', label: 'American'},
      {id: 'anatolian', label: 'Anatolian'},
      {id: 'arabic', label: 'Arabic'},
      {id: 'british', label: 'British'},
      {id: 'canadian', label: 'Canadian'},
      {id: 'chinese', label: 'Chinese'},
      {id: 'colombian', label: 'Colombian'},
      {id: 'european', label: 'European'},
      {id: 'filipino', label: 'Filipino'},
      {id: 'french', label: 'French'},
      {id: 'german', label: 'German'},
      {id: 'indian', label: 'Indian'},
      {id: 'istanbul', label: 'Istanbul'},
      {id: 'japanese', label: 'Japanese'},
      {id: 'latin american', label: 'Latin American'},
      {id: 'mexican', label: 'Mexican'},
      {id: 'middle eastern', label: 'Middle Eastern'},
      {id: 'new york', label: 'New York'},
      {id: 'nigerian', label: 'Nigerian'},
      {id: 'northern', label: 'Northern'},
      {id: 'parisian', label: 'Parisian'},
      {id: 'received pronunciation', label: 'Received Pronunciation'},
      {id: 'russian', label: 'Russian'},
      {id: 'seoul', label: 'Seoul'},
      {id: 'singaporean', label: 'Singaporean'},
      {id: 'south african', label: 'South African'},
      {id: 'southern', label: 'Southern'},
      {id: 'spanish', label: 'Spanish'},
      {id: 'us midwest', label: 'US Midwest'},
      {id: 'us southern', label: 'US Southern'},
    ],
  },
  country: {
    id: 'country',
    label: 'Country',
    options: [
      {id: 'Canada', label: 'Canada'},
      {id: 'China', label: 'China'},
      {id: 'Colombia', label: 'Colombia'},
      {id: 'Europe', label: 'Europe'},
      {id: 'France', label: 'France'},
      {id: 'Germany', label: 'Germany'},
      {id: 'India', label: 'India'},
      {id: 'Italy', label: 'Italy'},
      {id: 'Japan', label: 'Japan'},
      {id: 'Mexico', label: 'Mexico'},
      {id: 'Nigeria', label: 'Nigeria'},
      {id: 'Philippines', label: 'Philippines'},
      {id: 'Russia', label: 'Russia'},
      {id: 'Saudi Arabia', label: 'Saudi Arabia'},
      {id: 'Singapore', label: 'Singapore'},
      {id: 'South Africa', label: 'South Africa'},
      {id: 'South Korea', label: 'South Korea'},
      {id: 'Spain', label: 'Spain'},
      {id: 'Sweden', label: 'Sweden'},
      {id: 'Turkey', label: 'Turkey'},
      {id: 'United Arab Emirates', label: 'United Arab Emirates'},
      {id: 'United Kingdom', label: 'United Kingdom'},
      {id: 'United States', label: 'United States'},
    ],
  },
};

// Voice-only filter definitions
export const VOICE_ONLY_FILTERS: Record<
  string,
  Omit<AvatarFilter, 'selectedOptionId'>
> = {
  gender: {
    id: 'gender',
    label: 'Gender',
    options: [
      {id: 'Feminine', label: 'Feminine'},
      {id: 'Masculine', label: 'Masculine'},
    ],
  },
  age: {
    id: 'age',
    label: 'Age',
    options: [
      {id: 'Young Adults', label: 'Young Adults (18-39)'},
      {id: 'Middle-Aged Adults', label: 'Middle-Aged Adults (40-64)'},
      {id: 'Seniors', label: 'Seniors (65+)'},
    ],
  },
  useCase: {
    id: 'useCase',
    label: 'Use Case',
    options: [
      {id: 'advertisement', label: 'Advertisement'},
      {id: 'conversational', label: 'Conversational'},
      {id: 'entertainment_tv', label: 'Entertainment TV'},
      {id: 'informative_educational', label: 'Informative Educational'},
      {id: 'narrative_story', label: 'Narrative Story'},
      {id: 'social_media', label: 'Social Media'},
    ],
  },
  category: {
    id: 'category',
    label: 'Category',
    options: [{id: 'professional', label: 'Professional'}],
  },
  accent: {
    id: 'accent',
    label: 'Accent',
    options: [
      {id: 'american', label: 'American'},
      {id: 'us midwest', label: 'US Midwest'},
      {id: 'british', label: 'British'},
    ],
  },
};

// Filter combinations for different avatar types
export const VOICE_ONLY_FILTER_KEYS = [
  'gender',
  'age',
  'useCase',
  'accent',
] as const;

export const LIBRARY_FILTER_KEYS = [
  'gender',
  'age',
  'role',
  'presentationStyle',
  'culturalRepresentation',
] as const;

// Helper function to create filter arrays from keys
export function createFiltersFromKeys(
  filterKeys: readonly string[],
  filterType: 'avatar' | 'voice' = 'avatar'
): Omit<AvatarFilter, 'selectedOptionId'>[] {
  const filterSource =
    filterType === 'voice' ? VOICE_ONLY_FILTERS : AVATAR_FILTERS;
  return filterKeys.map((key) => filterSource[key]);
}
